/**
 * Where a panel sits in the Operator: a column (left, middle, right) and the end of it (top, bottom) —
 * notitia-engine's `PanelColumn` and `PanelAlign`, which a panel, a tab and an Alternis chat may carry
 * as `column` and `align`.
 *
 * THE ONE TABLE OF DEFAULTS. The engine sends only what the author set: no default is stamped into a
 * payload, so a process built before placement existed, and a snapshot restored from one, carry
 * nothing — and are placed by the same rule as a new process whose author placed nothing. This is that
 * rule, for both surfaces that need it: the Designer, which says what an unset placement means
 * ("Default (Left)"), and the Operator, which lays the panels out by it. Two copies would drift, and
 * a panel would then sit somewhere other than where the Designer said it would.
 */

export const PANEL_COLUMNS = ["left", "middle", "right"] as const;
export type PanelColumn = (typeof PANEL_COLUMNS)[number];

export const PANEL_ALIGNS = ["top", "bottom"] as const;
export type PanelAlign = (typeof PANEL_ALIGNS)[number];

export interface Placement {
	column: PanelColumn;
	align: PanelAlign;
}

/**
 * What can be placed, by what it is: a panel's own kind (`graphly`, `lumina`, `question_list`,
 * `suggestion_pool`), a web tab (`tab`) or a chat (`alternis_chat`).
 */
export type PlacedKind =
	| "graphly"
	| "lumina"
	| "question_list"
	| "suggestion_pool"
	| "tab"
	| "alternis_chat";

/**
 * Where each kind goes when its action places it nowhere. The chats keep the left, the diagram and
 * the views the middle, the question list the right — the layout the Operator had before placement —
 * and a suggestion pool sits under the question list, whose answers it fills.
 */
export const DEFAULT_PLACEMENT: Readonly<Record<PlacedKind, Placement>> = Object.freeze({
	alternis_chat: { column: "left", align: "top" },
	graphly: { column: "middle", align: "top" },
	lumina: { column: "middle", align: "top" },
	tab: { column: "middle", align: "top" },
	question_list: { column: "right", align: "top" },
	suggestion_pool: { column: "right", align: "bottom" },
});

/** Where a kind goes by default; a kind this client does not know goes to the middle, at the top. */
export function defaultPlacement(kind: string | undefined): Placement {
	return (kind && DEFAULT_PLACEMENT[kind as PlacedKind]) || { column: "middle", align: "top" };
}

export function isPanelColumn(value: unknown): value is PanelColumn {
	return typeof value === "string" && (PANEL_COLUMNS as readonly string[]).includes(value);
}

export function isPanelAlign(value: unknown): value is PanelAlign {
	return typeof value === "string" && (PANEL_ALIGNS as readonly string[]).includes(value);
}

/**
 * A suggestion pool's old `position`, from a build before `column` and `align` replaced it, read
 * LITERALLY — `top-left` is left/top, `bottom-right` right/bottom — as the generator folds it when it
 * opens such a project. `null` for anything that was never one of the four corners.
 */
export function poolPositionPlacement(position: unknown): Placement | null {
	if (typeof position !== "string") return null;
	const [align, column, rest] = position.split("-");
	if (rest !== undefined || !isPanelAlign(align) || !isPanelColumn(column) || column === "middle")
		return null;
	return { column, align };
}

/**
 * Where something with `kind` and the given fields sits: its own `column` and `align`, each where it
 * is one of the set; else, for a pool from an older build, its `position`; else the kind's default —
 * half by half, so a panel that names only its column takes its kind's end. A value this client
 * does not know falls back to the default and never hides the panel.
 */
export function resolvePlacement(
	kind: string | undefined,
	fields: { column?: unknown; align?: unknown; position?: unknown },
): Placement {
	const fallback = poolPositionPlacement(fields.position) ?? defaultPlacement(kind);
	return {
		column: isPanelColumn(fields.column) ? fields.column : fallback.column,
		align: isPanelAlign(fields.align) ? fields.align : fallback.align,
	};
}
